import { useForm } from "react-hook-form";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useVidyoStore } from "@/features/vidyo-call/utils/vidyo-store";
import { isSessionConflict } from "@/types/error";
import { zodResolver } from "@hookform/resolvers/zod";

import { useExtendSession } from "../api/extend-session";
import {
  CustomExtendSessionFormData,
  customExtendSessionSchema,
} from "../types/custom-extend-session";

export function ExtendByCustomAmountForm() {
  const setConflictingSessions = useVidyoStore(
    (state) => state.moderationDrawer.setConflictingSessions,
  );
  const sessionId = useVidyoStore((state) => state.sessionId);
  const { mutateAsync: extendSession, isPending: isExtendingSession } =
    useExtendSession({
      onMutate: () => {
        setConflictingSessions(undefined);
      },
      onSuccess: () => {
        toast.success("Session extended");
        setConflictingSessions(undefined);
      },
      onError: (error) => {
        if (isSessionConflict(error)) {
          setConflictingSessions(error.conflictingSessions);
        } else {
          toast.error(error.message);
        }
      },
    });

  const form = useForm<CustomExtendSessionFormData>({
    resolver: zodResolver(customExtendSessionSchema),
    defaultValues: {
      minutes: 0,
    },
  });

  const handleSubmit = async (data: CustomExtendSessionFormData) => {
    await extendSession({
      sessionId,
      payload: {
        extensionInMinutes: data.minutes,
      },
    });
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <div className="flex items-start gap-2">
          <FormField
            control={form.control}
            name="minutes"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input {...field} type="text" placeholder="Enter minutes" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button
            variant="outline"
            type="submit"
            isLoading={isExtendingSession}
          >
            Extend
          </Button>
        </div>
      </form>
    </Form>
  );
}
